import styled from "styled-components";
import { useEffect, useState } from "react";
import {
  ExternalLink,
  MessageCircle,
  RefreshCw,
  RotateCcw,
  Ban,
  FileText,
  Download,
  Banknote,
  Undo2,
  Zap,
} from "lucide-react";

import { theme } from "../theme";
import { useT } from "../i18n";
import PayBadge from "../PayBadge";

// A payment AFTER it exists: the same dialog, second act.
//
// Everything the front desk can do to one payment is here and nowhere else. Previously these controls
// were scattered across the card that happened to own that carrier — so "sync" meant "sync the newest
// deposit payment", and a booking with two payments of the same kind had one of them permanently
// unreachable. Here the payment being acted on is the one on screen.
const Wrap = styled.div`
  padding: 0.2rem 0 0.3rem;
`;

const Head = styled.div`
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
`;

const Amount = styled.div`
  color: ${theme.ink};
  font-size: 1.4rem;
  font-weight: 800;
`;

const Chip = styled.span`
  align-items: center;
  background: ${(props) => (props.$xendit ? "#eef3fb" : "#f4f2ee")};
  border-radius: 999px;
  color: ${(props) => (props.$xendit ? "#2f4a70" : "#5e6278")};
  display: inline-flex;
  font-size: 0.7rem;
  font-weight: 800;
  gap: 0.2rem;
  padding: 0.1rem 0.45rem;
`;

const Rows = styled.div`
  border: 1px solid ${theme.border};
  border-radius: 10px;
  padding: 0.5rem 0.65rem;
`;

const Row = styled.div`
  display: flex;
  gap: 0.6rem;
  justify-content: space-between;
  padding: 0.16rem 0;
`;

const Key = styled.span`
  color: #4a4f63;
  font-size: 0.78rem;
  font-weight: 700;
`;

const Val = styled.span`
  color: ${theme.ink};
  font-size: 0.8rem;
  font-weight: 700;
  overflow-wrap: anywhere;
  text-align: right;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.7rem;
`;

const Btn = styled.button`
  align-items: center;
  background: #ffffff;
  border: 1px solid ${theme.border};
  border-radius: 8px;
  color: ${(props) => (props.$danger ? "#b42318" : theme.ink700)};
  cursor: pointer;
  display: inline-flex;
  font-size: 0.8rem;
  font-weight: 800;
  gap: 0.28rem;
  padding: 0.42rem 0.7rem;
  text-decoration: none;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`;

const Undo = styled.div`
  background: #fff6f5;
  border: 1px solid #f4c7c3;
  border-radius: 10px;
  margin-top: 0.7rem;
  padding: 0.6rem 0.7rem;
`;

const UndoText = styled.p`
  color: #7a271a;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.45;
  margin: 0 0 0.5rem;
`;

const ReasonInput = styled.textarea`
  border: 1px solid ${theme.border};
  border-radius: 8px;
  box-sizing: border-box;
  color: ${theme.ink};
  font-family: inherit;
  font-size: 0.85rem;
  min-height: 56px;
  padding: 0.4rem 0.55rem;
  resize: vertical;
  width: 100%;
`;

const RECREATABLE = ["expired", "failed"];
// The server's MIN_REASON_LENGTH. Mirrored so the button stays disabled until the reason would pass.
const MIN_REASON_LENGTH = 5;

// Ticks once a second so the undo button hides itself when the window closes. The server checks the
// same window when the button is pressed, so this is a courtesy, never the guard.
const useCountdown = (untilIso) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!untilIso) return undefined;
    const deadline = new Date(untilIso).getTime();
    if (Number.isNaN(deadline) || deadline <= Date.now()) return undefined;

    const id = setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= deadline) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [untilIso]);

  if (!untilIso) return 0;
  const remaining = Math.ceil((new Date(untilIso).getTime() - now) / 1000);
  return remaining > 0 ? remaining : 0;
};

const formatRemaining = (seconds) =>
  `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const PaymentDetail = (props) => {
  const { t } = useT();
  const payment = props.payment || {};
  const live =
    payment.status === "pending" && Boolean(payment.payment_link_url);
  const [undoing, setUndoing] = useState(false);
  const [reason, setReason] = useState("");
  const undoLeft = useCountdown(payment.manual_void_until);
  const undoable = undoLeft > 0;
  const reasonValid = reason.trim().length >= MIN_REASON_LENGTH;
  const voided = payment.manual_void;
  const pdfBase = `/admin/online_payments/${payment.id}/invoice_pdf?type=RoomBookingPayment`;

  const label = payment.add_on_only
    ? t("sec_add_on")
    : payment.deposit_collection
      ? t("sec_deposit")
      : t("sec_payment");

  return (
    <Wrap>
      <Head>
        <Amount>{payment.amount_formatted}</Amount>
        <PayBadge status={payment.status} />
        <Chip $xendit={payment.provider === "xendit"}>
          {payment.provider === "xendit" ? (
            <Zap size={11} />
          ) : (
            <Banknote size={11} />
          )}
          {payment.provider === "xendit"
            ? "Xendit"
            : payment.payment_method || t("provider_manual")}
        </Chip>
      </Head>

      <Rows>
        <Row>
          <Key>{t("pay_kind")}</Key>
          <Val>{label}</Val>
        </Row>
        {payment.invoice_no ? (
          <Row>
            <Key>{t("invoice_no")}</Key>
            <Val>{payment.invoice_no}</Val>
          </Row>
        ) : null}
        {payment.paid_at ? (
          <Row>
            <Key>{t("pay_paid_at")}</Key>
            <Val>{new Date(payment.paid_at).toLocaleString("id-ID")}</Val>
          </Row>
        ) : null}
        {payment.expires_at && payment.status === "pending" ? (
          <Row>
            <Key>{t("pay_expires_at")}</Key>
            <Val>{new Date(payment.expires_at).toLocaleString("id-ID")}</Val>
          </Row>
        ) : null}
        {voided ? (
          <>
            <Row>
              <Key>{t("pay_undo_done")}</Key>
              <Val>
                {[
                  voided.by,
                  voided.at
                    ? new Date(voided.at).toLocaleString("id-ID")
                    : null,
                ]
                  .filter(Boolean)
                  .join(", ")}
              </Val>
            </Row>
            <Row>
              <Key>{t("pay_undo_reason")}</Key>
              <Val>{voided.reason}</Val>
            </Row>
          </>
        ) : null}
      </Rows>

      <Actions>
        {live ? (
          <>
            <Btn
              as="a"
              href={payment.payment_link_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <ExternalLink size={13} /> {t("open_link")}
            </Btn>
            {/* wa.me only: the FO sends from their own WhatsApp, so nothing leaves the server. */}
            <Btn
              type="button"
              disabled={props.busy}
              onClick={() => props.onAction("send_whatsapp")}
            >
              <MessageCircle size={13} /> {t("send_payment_wa")}
            </Btn>
          </>
        ) : null}

        {payment.provider === "xendit" && payment.status !== "paid" ? (
          <Btn
            type="button"
            disabled={props.busy}
            onClick={() => props.onAction("sync")}
          >
            <RefreshCw size={13} /> {t("pay_sync")}
          </Btn>
        ) : null}

        {/* Expired/failed only — RoomBookingPayments::RegeneratePaymentLinkService accepts nothing
            else, and a button that always returns an error is the dead end this screen exists to
            remove. To change what a LIVE link charges: void it, then build a new one. */}
        {RECREATABLE.includes(payment.status) ? (
          <Btn
            type="button"
            disabled={props.busy}
            onClick={() => props.onAction("recreate")}
          >
            <RotateCcw size={13} /> {t("pay_recreate")}
          </Btn>
        ) : null}

        {/* A paid payment is not voided by this button: that is a refund, with its own ledger and
         * its own guest-facing flow. The only exception is the undo below.
         */}
        {payment.status === "pending" ? (
          <Btn
            type="button"
            $danger
            disabled={props.busy}
            onClick={() => props.onAction("void")}
          >
            <Ban size={13} /> {t("pay_void")}
          </Btn>
        ) : null}

        {/* The exception: a manual payment recorded by mistake, for a few minutes after it was
         * recorded. No money changed hands, so this is not a refund.
         */}
        {undoable && !undoing ? (
          <Btn
            type="button"
            $danger
            disabled={props.busy}
            onClick={() => setUndoing(true)}
          >
            <Undo2 size={13} /> {t("pay_undo")} ({formatRemaining(undoLeft)})
          </Btn>
        ) : null}

        {payment.invoice_pdf ? (
          <>
            <Btn
              as="a"
              href={pdfBase}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FileText size={13} /> {t("view_pdf")}
            </Btn>
            <Btn
              as="a"
              href={`${pdfBase}&download=1`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Download size={13} /> {t("download_pdf")}
            </Btn>
          </>
        ) : null}
      </Actions>

      {undoable && undoing ? (
        <Undo>
          <UndoText>
            {payment.amount_formatted}: {t("pay_undo_body")}
          </UndoText>
          <ReasonInput
            value={reason}
            placeholder={t("pay_undo_reason_placeholder")}
            aria-label={t("pay_undo_reason")}
            onChange={(event) => setReason(event.target.value)}
          />
          <Actions>
            <Btn
              type="button"
              disabled={props.busy}
              onClick={() => setUndoing(false)}
            >
              {t("pay_undo_back")}
            </Btn>
            <Btn
              type="button"
              $danger
              disabled={props.busy || !reasonValid}
              onClick={() =>
                props.onAction("void_manual", { reason: reason.trim() })
              }
            >
              <Undo2 size={13} /> {t("pay_undo_confirm")} (
              {formatRemaining(undoLeft)})
            </Btn>
          </Actions>
        </Undo>
      ) : null}
    </Wrap>
  );
};

export default PaymentDetail;
